/*******************************************************************************
 * Copyright (c) 2025 Bula Technologies
 *
 * All rights reserved.
 *
 * Contributors:
 *  James Worthington - Initial implementation
 *  Amina Rais - Massive Improvements
 *  Version: 1.0.0
 *
 *******************************************************************************/
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Divider,
  alpha,
} from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SportsGolfRoundedIcon from "@mui/icons-material/SportsGolfRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import {
  callAcceptrandomusersforbet,
  calllistbetsfortesting,
  callGetcompletedbettestsummary,
  callValidateCompletedBetSettlement
} from "../../../../services/bet/BetController";
import { formatFirestoreTimestamp } from "../../../../utils/formatTimestampToDate";
import BackButton from "../../../../components/shared/Buttons/BackButton";
import CompletedBetSummaryModal from "./views/CompletedBetSummaryModal";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  LinearProgress,
  Tooltip,
} from "@mui/material";

import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
const AutomatedTestingPage = () => {
  const [loading, setLoading] = useState(false);
  const [betsLoading, setBetsLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [validationLoading, setValidationLoading] = useState(false);
  const [bets, setBets] = useState([]);
  const [selectedBetId, setSelectedBetId] = useState("");
  const [targetRandomAccepts, setTargetRandomAccepts] = useState(49);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [betSummary, setBetSummary] = useState(null);
  const settlementCheckDefinitions = [
    {
      key: "releaseStakeMatchesAcceptedCount",
      title: "Every accepted participant received their stake back",
      description:
        "The number of Release Stake ledger records should match the number of users who accepted the bet.",
      getActual: (result) => result.ledgerSummary?.releaseStakeCount ?? 0,
      getExpected: (result) => result.expected?.releaseStakeCount ?? 0,
    },
    {
      key: "transferInMatchesTransferOut",
      title: "Money transferred out matches money transferred in",
      description:
        "Every payout leaving one balance should have a matching amount entering another balance.",
      getActual: (result) =>
        `${result.ledgerSummary?.transferOutCount ?? 0} out / ${result.ledgerSummary?.transferInCount ?? 0
        } in`,
      getExpected: () => "Both values should match",
    },
    {
      key: "platformFeeMatchesWinnersWithProfit",
      title: "Platform fees were created correctly",
      description:
        "A platform fee should only be created for winners who received a profit.",
      getActual: (result) => result.ledgerSummary?.platformFeeCount ?? 0,
      getExpected: (result) => result.expected?.platformFeeCount ?? 0,
    },
  ];

  const selectedBet = useMemo(
    () => bets.find((bet) => bet.betID === selectedBetId) || null,
    [bets, selectedBetId]
  );
  const isValidationResult = result?.action === "validate_settlement";

  const allSettlementChecksPassed =
    isValidationResult &&
    result?.checks &&
    Object.values(result.checks).every(Boolean);


  const formatMoney = (value) => {
    const amount = Number(value || 0);

    return amount.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };
  const handleValidateSettlement = async () => {
    if (!selectedBetId) return;

    setValidationLoading(true);
    setError("");
    setResult(null);

    try {
      const response = await callValidateCompletedBetSettlement({
        betID: selectedBetId,
      });

      setResult({
        action: "validate_settlement",
        ...response,
      });
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to validate settlement.");
    } finally {
      setValidationLoading(false);
    }
  };
  const fetchListbetsfortesting = async () => {
    setBetsLoading(true);
    setError("");

    try {
      const response = await calllistbetsfortesting({
        limit: 100,
      });

      const fetchedBets = response?.bets || [];
      setBets(fetchedBets);

      if (fetchedBets.length > 0 && !selectedBetId) {
        setSelectedBetId(fetchedBets[0].betID);
      }
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to load bets.");
    } finally {
      setBetsLoading(false);
    }
  };

  useEffect(() => {
    fetchListbetsfortesting();
  }, []);

  const handleAcceptUsers = async () => {
    setLoading(true);
    setError("");
    setResult(null);

    try {
      const response = await callAcceptrandomusersforbet({
        betID: selectedBetId,
        targetRandomAccepts: Number(targetRandomAccepts),
      });

      setResult(response);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to accept random users.");
    } finally {
      setLoading(false);
    }
  };

  const handleViewSummary = async () => {
    if (!selectedBetId) return;

    setSummaryLoading(true);
    setError("");

    try {
      const response = await callGetcompletedbettestsummary({
        betID: selectedBetId,
      });

      setBetSummary(response);
      setSummaryModalOpen(true);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to load completed bet summary.");
    } finally {
      setSummaryLoading(false);
    }
  };

  return (
    <Box sx={{ px: { xs: 2, md: 5 }, py: 4 }}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <BackButton pageTitle="Automated Testing" />
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: "0 20px 50px rgba(0,0,0,0.18)",
              border: "1px solid",
              borderColor: alpha("#ff5a3d", 0.12),
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h5" fontWeight={800}>
                    Configure Test Run
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.8}>
                    Select a bet and choose how many users should be accepted automatically.
                  </Typography>
                </Box>

                {error && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: "rgba(244, 67, 54, 0.12)",
                      border: "1px solid rgba(244, 67, 54, 0.35)",
                      color: "#ffb3b3",
                    }}
                  >
                    <Typography variant="body2">{error}</Typography>
                  </Box>
                )}

                <FormControl fullWidth disabled={betsLoading || loading || summaryLoading || validationLoading}>
                  <InputLabel id="bet-select-label">Select Bet</InputLabel>
                  <Select
                    labelId="bet-select-label"
                    value={selectedBetId}
                    label="Select Bet"
                    onChange={(e) => setSelectedBetId(e.target.value)}
                    sx={{
                      borderRadius: 3,
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: 3,
                          mt: 1,
                        },
                      },
                    }}
                  >
                    {bets.map((bet) => (
                      <MenuItem key={bet.betID} value={bet.betID}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ width: "100%", gap: 2 }}
                        >
                          <Typography fontWeight={600}>
                            {bet.betName || "-"}  {"   "} {bet.betStatus || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {bet.createdDate
                              ? formatFirestoreTimestamp(bet.createdDate)
                              : "-"}
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12}>
                    <TextField
                      label="Target Random Accepts"
                      type="number"
                      fullWidth
                      value={targetRandomAccepts}
                      onChange={(e) => setTargetRandomAccepts(e.target.value)}
                      disabled={loading || validationLoading}
                      helperText="How many invited users to randomly accept in this run."
                      InputProps={{
                        sx: { borderRadius: 3 },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        bgcolor: "rgba(255, 90, 61, 0.06)",
                        border: "1px solid rgba(255, 90, 61, 0.2)",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        • <strong>Target Random Accepts</strong> = how many users this script will try to accept now.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={fetchListbetsfortesting}
                      disabled={
                        betsLoading ||
                        loading ||
                        summaryLoading ||
                        validationLoading
                      }
                      startIcon={
                        betsLoading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <RefreshRoundedIcon />
                        )
                      }
                      sx={{
                        minHeight: 46,
                        borderRadius: "999px",
                        borderColor: alpha("#ff5a3d", 0.55),
                        color: "#ff5a3d",
                        whiteSpace: "nowrap",
                        "&:hover": {
                          borderColor: "#ff5a3d",
                          bgcolor: alpha("#ff5a3d", 0.06),
                        },
                      }}
                    >
                      Refresh Bets
                    </Button>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleAcceptUsers}
                      disabled={
                        !selectedBetId ||
                        loading ||
                        validationLoading ||
                        summaryLoading ||
                        betsLoading
                      }
                      startIcon={
                        loading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <PlayArrowRoundedIcon />
                        )
                      }
                      sx={{
                        minHeight: 46,
                        borderRadius: "999px",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        background:
                          "linear-gradient(135deg, #ff6a4d 0%, #ff4b2b 100%)",
                        boxShadow: "0 10px 25px rgba(255,90,61,0.35)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #ff7256 0%, #ff4b2b 100%)",
                        },
                      }}
                    >
                      Accept Users
                    </Button>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleViewSummary}
                      disabled={!selectedBetId || summaryLoading}
                      startIcon={
                        summaryLoading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <VisibilityRoundedIcon />
                        )
                      }
                      sx={{
                        minHeight: 46,
                        borderRadius: "999px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      View Summary
                    </Button>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleValidateSettlement}
                      disabled={
                        !selectedBetId ||
                        validationLoading ||
                        loading ||
                        summaryLoading
                      }
                      startIcon={
                        validationLoading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <CheckCircleRoundedIcon />
                        )
                      }
                      sx={{
                        minHeight: 46,
                        borderRadius: "999px",
                        borderColor: alpha("#4caf50", 0.55),
                        color: "#4caf50",
                        whiteSpace: "nowrap",
                        "&:hover": {
                          borderColor: "#4caf50",
                          bgcolor: alpha("#4caf50", 0.06),
                        },
                      }}
                    >
                      Validate Settlement
                    </Button>
                  </Grid>
                </Grid>

                {selectedBet && (
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      background:
                        "linear-gradient(180deg, rgba(255,90,61,0.07) 0%, rgba(255,90,61,0.02) 100%)",
                      border: "1px solid",
                      borderColor: alpha("#ff5a3d", 0.18),
                    }}
                  >
                    <Stack spacing={1.2}>
                      <Typography variant="subtitle1" fontWeight={800}>
                        Selected Bet
                      </Typography>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <SportsGolfRoundedIcon sx={{ color: "#ff5a3d", fontSize: 18 }} />
                        <Typography variant="body2">
                          <strong>Name:</strong> {selectedBet.betName || "Untitled Bet"}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <AccessTimeRoundedIcon sx={{ color: "#ff5a3d", fontSize: 18 }} />
                        <Typography variant="body2">
                          <strong>Created:</strong>{" "}
                          {selectedBet.createdDate
                            ? formatFirestoreTimestamp(selectedBet.createdDate)
                            : "-"}
                        </Typography>
                      </Stack>

                      <Typography
                        variant="body2"
                        sx={{
                          wordBreak: "break-all",
                          color: "text.secondary",
                          pt: 0.5,
                        }}
                      >
                        <strong>Bet ID:</strong> {selectedBet.betID}
                      </Typography>
                    </Stack>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: "0 20px 50px rgba(0,0,0,0.18)",
              border: "1px solid",
              borderColor: alpha("#ff5a3d", 0.12),
              minHeight: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h5" fontWeight={800}>
                    Result
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.8}>
                    {isValidationResult
                      ? "Review settlement row counts, ledger totals, and amount reconciliation for the selected completed bet."
                      : "Review the automated acceptance outcome and any failed users."}
                  </Typography>
                </Box>

                <Divider />

                {!result ? (
                  <Box
                    sx={{
                      minHeight: 260,
                      borderRadius: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      border: "1px dashed",
                      borderColor: alpha("#999", 0.35),
                      bgcolor: alpha("#fff", 0.03),
                    }}
                  >
                    <SportsGolfRoundedIcon
                      sx={{ fontSize: 42, color: "text.secondary", mb: 1 }}
                    />
                    <Typography variant="body1" color="text.secondary">
                      No result yet.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Run a test to see the response here.
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={2}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      flexWrap="wrap"
                    >
                      <Chip
                        icon={
                          result.success && (!isValidationResult || allSettlementChecksPassed) ? (
                            <CheckCircleRoundedIcon />
                          ) : (
                            <ErrorOutlineRoundedIcon />
                          )
                        }
                        label={
                          isValidationResult
                            ? allSettlementChecksPassed
                              ? "Settlement Valid"
                              : "Settlement Issues Found"
                            : result.success
                              ? "Completed"
                              : "Completed with Errors"
                        }
                        color={
                          result.success && (!isValidationResult || allSettlementChecksPassed)
                            ? "success"
                            : "warning"
                        }
                        sx={{ borderRadius: "999px", fontWeight: 700 }}
                      />
                      {!isValidationResult && (
                        <>
                          <Chip
                            label={`Accepted Before: ${result.acceptedBefore ?? 0}`}
                            sx={{ borderRadius: "999px" }}
                          />
                          <Chip
                            label={`Accepted After: ${result.acceptedAfter ?? 0}`}
                            sx={{ borderRadius: "999px" }}
                          />
                          <Chip
                            label={`Success: ${result.successCount ?? 0}`}
                            color="success"
                            variant="outlined"
                            sx={{ borderRadius: "999px" }}
                          />
                          <Chip
                            label={`Failed: ${result.failedCount ?? 0}`}
                            color={(result.failedCount ?? 0) > 0 ? "error" : "default"}
                            variant="outlined"
                            sx={{ borderRadius: "999px" }}
                          />
                        </>
                      )}
                      {isValidationResult && (
                        <>
                          <Chip
                            label={`Accepted: ${result.acceptedCount ?? 0}`}
                            sx={{ borderRadius: "999px" }}
                          />
                          <Chip
                            label={`Winners: ${result.winnerCount ?? 0}`}
                            sx={{ borderRadius: "999px" }}
                          />
                          <Chip
                            label={`Total Ledger Rows: ${result.ledgerSummary?.totalRecords ?? 0}`}
                            variant="outlined"
                            sx={{ borderRadius: "999px" }}
                          />

                          <Chip
                            label={`Settlement Rows: ${result.ledgerSummary?.settlementRecordCount ?? 0}`}
                            color="success"
                            variant="outlined"
                            sx={{ borderRadius: "999px" }}
                          />

                          <Chip
                            label={`Bet Place Rows: ${result.ledgerSummary?.betPlaceCount ?? 0}`}
                            variant="outlined"
                            sx={{ borderRadius: "999px" }}
                          />
                          <Chip
                            label={`Status: ${result.betStatus || "-"}`}
                            color={result.betStatus === "Completed" ? "success" : "warning"}
                            variant="outlined"
                            sx={{ borderRadius: "999px" }}
                          />
                        </>
                      )}
                    </Stack>

                    {isValidationResult ? (
                      <Stack spacing={3}>
                        <Box
                          sx={{
                            p: 2.25,
                            borderRadius: 3,
                            bgcolor: allSettlementChecksPassed
                              ? alpha("#4caf50", 0.1)
                              : alpha("#ff9800", 0.1),
                            border: "1px solid",
                            borderColor: allSettlementChecksPassed
                              ? alpha("#4caf50", 0.35)
                              : alpha("#ff9800", 0.35),
                          }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            {allSettlementChecksPassed ? (
                              <CheckCircleRoundedIcon sx={{ color: "#4caf50", mt: 0.2 }} />
                            ) : (
                              <WarningAmberRoundedIcon sx={{ color: "#ff9800", mt: 0.2 }} />
                            )}

                            <Box>
                              <Typography fontWeight={800}>
                                {allSettlementChecksPassed
                                  ? "Settlement validation passed"
                                  : "Settlement validation found issues"}
                              </Typography>

                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {allSettlementChecksPassed
                                  ? "All participant counts, transfers, and platform-fee records match the expected settlement results."
                                  : "One or more settlement checks did not match the expected result. Review the failed checks below before approving this settlement."}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>

                        <Box>
                          <Typography variant="h6" fontWeight={800}>
                            What was validated?
                          </Typography>

                          <Typography variant="body2" color="text.secondary" mt={0.5}>
                            These checks confirm that accepted users, winner payouts,
                            returned stakes, and platform fees were recorded correctly
                            in the ledger.
                          </Typography>
                        </Box>

                        <Grid container spacing={2} alignItems="stretch">
                          {settlementCheckDefinitions.map((check) => {
                            const passed = Boolean(result.checks?.[check.key]);

                            return (
                              <Grid item xs={12} md={4} key={check.key} sx={{ display: "flex" }}
                              >
                                <ValidationCheckCard
                                  passed={passed}
                                  title={check.title}
                                  description={check.description}
                                  actual={check.getActual(result)}
                                  expected={check.getExpected(result)}
                                />
                              </Grid>
                            );
                          })}
                        </Grid>

                        <Divider />

                        <Box>
                          <Typography variant="h6" fontWeight={800}>
                            Settlement overview
                          </Typography>

                          <Typography variant="body2" color="text.secondary" mt={0.5}>
                            This section shows how many users participated, how many
                            winners were selected, and how many ledger records were
                            created.
                          </Typography>
                        </Box>

                        <Grid
                          container
                          spacing={2}
                          alignItems="stretch"
                          sx={{ mb: 2 }}
                        >
                          <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                            <Box
                              sx={{
                                p: 2,
                                width: "100%",
                                minHeight: 150,
                                boxSizing: "border-box",
                                borderRadius: 3,
                                bgcolor: alpha("#2196f3", 0.08),
                                border: "1px solid",
                                borderColor: alpha("#2196f3", 0.2),
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                Accepted participants
                              </Typography>

                              <Typography variant="h5" fontWeight={800}>
                                {result.acceptedCount ?? 0}
                              </Typography>

                              <Typography variant="body2" color="text.secondary">
                                Users who joined the bet and were included in settlement.
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                            <Box
                              sx={{
                                p: 2,
                                width: "100%",
                                minHeight: 150,
                                boxSizing: "border-box",
                                borderRadius: 3,
                                bgcolor: alpha("#4caf50", 0.08),
                                border: "1px solid",
                                borderColor: alpha("#4caf50", 0.2),
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                Winners
                              </Typography>

                              <Typography variant="h5" fontWeight={800}>
                                {result.winnerCount ?? 0}
                              </Typography>

                              <Typography variant="body2" color="text.secondary">
                                Participants who received a winning payout.
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                            <Box
                              sx={{
                                p: 2,
                                width: "100%",
                                minHeight: 150,
                                boxSizing: "border-box",
                                borderRadius: 3,
                                bgcolor: alpha("#9c27b0", 0.08),
                                border: "1px solid",
                                borderColor: alpha("#9c27b0", 0.2),
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                Settlement records
                              </Typography>

                              <Typography variant="h5" fontWeight={800}>
                                {result.ledgerSummary?.settlementRecordCount ?? 0}
                              </Typography>

                              <Typography variant="body2" color="text.secondary">
                                Ledger records created when the bet was settled.
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                            <Box
                              sx={{
                                p: 2,
                                width: "100%",
                                minHeight: 150,
                                boxSizing: "border-box",
                                borderRadius: 3,
                                bgcolor: alpha("#ff9800", 0.08),
                                border: "1px solid",
                                borderColor: alpha("#ff9800", 0.2),
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                Bet status
                              </Typography>

                              <Typography variant="h5" fontWeight={800}>
                                {result.betStatus || "-"}
                              </Typography>

                              <Typography variant="body2" color="text.secondary">
                                The bet should be marked Completed after settlement.
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                        <Box sx={{ py: 1 }}>
                          <Divider
                            sx={{
                              borderColor: alpha("#ffffff", 0.14),
                            }}
                          />
                        </Box>
                        <Box  >
                          <Typography variant="h6" fontWeight={800}>
                            Money movement
                          </Typography>

                          <Typography variant="body2" color="text.secondary" mt={0.5}>
                            These values show where the money came from and where it
                            went during the bet and settlement process.
                          </Typography>
                        </Box>

                        <Grid container spacing={2} alignItems="stretch">
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            sx={{ display: "flex" }}
                          >
                            <FinancialTotalCard
                              title="Original bet stakes"
                              amount={formatMoney(
                                result.ledgerSummary?.amounts?.betPlaceTotal
                              )}
                              rows={`${result.ledgerSummary?.betPlaceCount ?? 0} ledger records`}
                              description="The total amount originally committed by participants when they joined the bet."
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <FinancialTotalCard
                              title="Stakes returned"
                              amount={formatMoney(
                                result.ledgerSummary?.amounts?.releaseStakeTotal
                              )}
                              rows={`${result.ledgerSummary?.releaseStakeCount ?? 0} ledger records`}
                              description="The total original stake returned to participants during settlement."
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <FinancialTotalCard
                              title="Platform fees"
                              amount={formatMoney(
                                result.ledgerSummary?.amounts?.platformFeeTotal
                              )}
                              rows={`${result.ledgerSummary?.platformFeeCount ?? 0} ledger records`}
                              description="The total fee collected by the platform from applicable winning payouts."
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <FinancialTotalCard
                              title="Money transferred out"
                              amount={formatMoney(
                                result.ledgerSummary?.amounts?.transferOutTotal
                              )}
                              rows={`${result.ledgerSummary?.transferOutCount ?? 0} ledger records`}
                              description="Money removed from source balances before being credited to recipients."
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <FinancialTotalCard
                              title="Money transferred in"
                              amount={formatMoney(
                                result.ledgerSummary?.amounts?.transferInTotal
                              )}
                              rows={`${result.ledgerSummary?.transferInCount ?? 0} ledger records`}
                              description="Money credited to participant or destination balances."
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <FinancialTotalCard
                              title="Total settlement activity"
                              amount={formatMoney(
                                result.ledgerSummary?.amounts
                                  ?.settlementActivityTotal ??
                                result.ledgerSummary?.amounts?.settlementTotal
                              )}
                              rows="Gross ledger activity"
                              description="The combined value of settlement ledger activity. This is not the bet value because both outgoing and incoming transfers are included."
                            />
                          </Grid>
                        </Grid>

                        <Box
                          sx={{
                            p: 2,
                            width: "100%",
                            minHeight: 150,
                            boxSizing: "border-box",
                            borderRadius: 3,
                            bgcolor: alpha("#2196f3", 0.08),
                            border: "1px solid",
                            borderColor: alpha("#2196f3", 0.2),
                          }}
                        >
                          <Stack direction="row" spacing={1.25} alignItems="flex-start">
                            <InfoOutlinedIcon sx={{ color: "#2196f3", mt: 0.15 }} />

                            <Typography variant="body2" color="text.secondary">
                              <strong>Important:</strong> Total settlement activity can be
                              higher than the actual bet amount because the ledger records both
                              sides of every transfer. Money leaving one balance and entering
                              another balance are stored as separate records.
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>
                    ) : (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 3,
                              bgcolor: alpha("#4caf50", 0.08),
                              border: "1px solid",
                              borderColor: alpha("#4caf50", 0.18),
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              Bet Status After
                            </Typography>
                            <Typography variant="h6" fontWeight={800}>
                              {result.betStatusAfter || "-"}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 3,
                              bgcolor: alpha("#ff9800", 0.08),
                              border: "1px solid",
                              borderColor: alpha("#ff9800", 0.18),
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              Bet Pool After
                            </Typography>
                            <Typography variant="h6" fontWeight={800}>
                              {result.betPoolAfter ?? "-"}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    )}

                    <Accordion
                      disableGutters
                      elevation={0}
                      sx={{
                        borderRadius: "12px !important",
                        border: "1px solid",
                        borderColor: alpha("#ffffff", 0.1),
                        bgcolor: alpha("#ffffff", 0.02),
                        "&:before": {
                          display: "none",
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreRoundedIcon />}
                      >
                        <Box>
                          <Typography fontWeight={700}>
                            Technical details
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            View the complete API response for debugging.
                          </Typography>
                        </Box>
                      </AccordionSummary>

                      <AccordionDetails>
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "#0f1117",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            fontFamily: "monospace",
                            fontSize: 13,
                            color: "#d8e1ff",
                            maxHeight: 420,
                            overflowY: "auto",
                          }}
                        >
                          {JSON.stringify(result, null, 2)}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <CompletedBetSummaryModal
        open={summaryModalOpen}
        onClose={() => setSummaryModalOpen(false)}
        betSummary={betSummary}
      />
    </Box>
  );
};

export default AutomatedTestingPage;

const ValidationCheckCard = ({
  passed,
  title,
  description,
  actual,
  expected,
}) => {
  const statusColor = passed ? "#4caf50" : "#f44336";

  return (
    <Box
      sx={{
        p: 2.25,
        width: "100%",
        borderRadius: 3,
        bgcolor: alpha(statusColor, 0.08),
        border: "1px solid",
        borderColor: alpha(statusColor, 0.3),
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.2} alignItems="flex-start">
          {passed ? (
            <CheckCircleRoundedIcon
              sx={{ color: statusColor, mt: 0.15 }}
            />
          ) : (
            <ErrorOutlineRoundedIcon
              sx={{ color: statusColor, mt: 0.15 }}
            />
          )}

          <Box>
            <Typography fontWeight={800}>
              {title}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {description}
            </Typography>
          </Box>
        </Stack>

        <Divider />

        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Actual result
            </Typography>

            <Typography fontWeight={800}>
              {actual}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Expected result
            </Typography>

            <Typography fontWeight={800}>
              {expected}
            </Typography>
          </Grid>
        </Grid>

        <Chip
          size="small"
          icon={
            passed
              ? <CheckCircleRoundedIcon />
              : <WarningAmberRoundedIcon />
          }
          label={passed ? "Check passed" : "Needs investigation"}
          color={passed ? "success" : "error"}
          sx={{
            alignSelf: "flex-start",
            fontWeight: 700,
          }}
        />
      </Stack>
    </Box>
  );
};
const FinancialTotalCard = ({
  title,
  amount,
  rows,
  description,
}) => (
  <Box
    sx={{
      p: 2.25,
      width: "100%",
      minHeight: 190,
      boxSizing: "border-box",
      borderRadius: 3,
      bgcolor: alpha("#2196f3", 0.06),
      border: "1px solid",
      borderColor: alpha("#2196f3", 0.2),
    }}
  >
    <Stack spacing={0.7}>
      <Stack direction="row" spacing={0.8} alignItems="center">
        <Typography variant="body2" fontWeight={700}>
          {title}
        </Typography>

        <Tooltip title={description} arrow>
          <InfoOutlinedIcon
            sx={{
              fontSize: 17,
              color: "text.secondary",
              cursor: "help",
            }}
          />
        </Tooltip>
      </Stack>

      <Typography variant="h5" fontWeight={800}>
        {amount}
      </Typography>

      <Typography variant="caption" color="text.secondary">
        {rows}
      </Typography>

      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Stack>
  </Box>
);